export const MENU_METADATA_CHANNELS = {
  DESKTOP: 'web',
  MOBILE: 'web-mobile',
};

export const INGREDIENTS_METADATA_COUNTRIES = {
  US: 'us'
}

export enum TagType {
  Promo = 'promo',
  Tagline = 'tagline',
  Rewards = 'rewards',
}

export interface MetadataTag {
  type: TagType | string;
  value: string;
}

export interface CustomizationMetadata {
  customizationId: number;
  alias: string;
  sortOrder: number;
  isHidden: boolean;
}

export interface MenuMetadataForChannel {
  channel: string;
  nutrition?: boolean;
  topLevelMenu: TopLevelMenu | null;
  groups: MenuItemGroup[];
  items: {
    [itemId: string]: MenuItemMetadata | undefined;
  };
  itemSections: {
    [name: string]: ItemSectionMetadata | undefined;
  };
  itemGroups: {
    [key: string]: MetadataItemGroup;
  };
  nonVariantSectionMap: {
    [key: string]: string;
  };
  dietaryTagGroups: DietaryTagGroup[];
  dietaryTagsLookup: {
    [key: string]: DietaryTagUI;
  };
}

export interface CalorieRange {
  min: number;
  max: number;
}

export interface MetadataItemGroup {
  displayName: string;
  defaultItemId: string;
  calories: CalorieRange;
  displayRangeFormat: boolean;
  customizeText: string;
}

export interface ItemSectionMetadata {
  name: string;
  alias: string;
  sortOrder: number;
  hasSelectNoneOption: boolean;
  items: {
    menuItemId: string;
    displayInputType?: string;
    sortOrder: number;
  }[];
}

export interface TopLevelMenu {
  channel: string;
  groups: TopLevelMenuGroup[];
}

export interface TopLevelMenuGroup {
  sortOrder: number;
  menuItemGroupId: string;
}

export enum MenuItemGroupType {
  Default = 'default',
  Nested = 'nested',
  Quantity = 'quantity',
  PreConfigured = 'preconfigured',
}

export interface BaseMenuItemGroup {
  id: string;
  includeAdditionalItemSections: boolean;
  itemSections: string[];
  displayName: string;
  menuItemType?: string;
  description: string;
  tags: MetadataTag[];
  thumbnailImageUrl: string;
  bannerImageUrl: string;
  items: {
    menuItemId: string;
    sortOrder: number;
  }[];
}

export interface DefaultMenuItemGroup extends BaseMenuItemGroup {
  type: MenuItemGroupType.Default;
}

export interface NestedMenuItemGroup extends BaseMenuItemGroup {
  type: MenuItemGroupType.Nested;
  groups: {
    menuItemGroupId: string;
    sortOrder: number;
  }[];
}

export interface QuantityMenuItemGroup extends BaseMenuItemGroup {
  type: MenuItemGroupType.Quantity;
  groups: {
    menuItemGroupId: string;
    sortOrder: number;
  }[];
}

export interface PreConfiguredMenuItemGroup extends BaseMenuItemGroup {
  type: MenuItemGroupType.PreConfigured;
}

export type MenuItemGroup = DefaultMenuItemGroup | NestedMenuItemGroup | QuantityMenuItemGroup | PreConfiguredMenuItemGroup;

export interface MenuItemMetadata {
  thumbnailUrl: string;
  tags: MetadataTag[];
  customizations: CustomizationMetadata[];
  nutrition: NutritionFact[];
  groupName?: string;
  showCustomizationPrice?: boolean;
  asCustomizationName?: string; // If the Menu Item is shown as a customization and a custom name is wanted for the selection
  showCustomizationNameWhenSelected?: boolean; // e.g. show "Extra" in UI instead of a check mark
  dietaryTags?: string[];
}

export interface DietaryTagGroup {
  id: string;
  groupName: string;
  badgeText: string;
  badgeColor: string;
  secondaryBadgeColor: string;
  subheader?: string;
  selectType: string;
  tags: DietaryTag[];
}

export interface DietaryTag {
  tagName: string;
  tagCode: string;
  preferredBadgeText?: string;
}

export interface DietaryTagUI {
  tagGroupId: string;
  badgeText: string;
  badgeColor: string;
  groupName: string;
}

export interface NutritionFact {
  name: NutritionFactName;
  value: string;
  unit: string;
}

export enum NutritionFactName {
  Calories = 'Calories',
  Portion = 'Portion',
}

export interface IngredientsMetadataForChannel {
  channel: string;
  ingredients: IngredientMetadata[];
  ingredientGroups: IngredientGroupMetadata[];
}

export interface IngredientMetadata {
  thumbnailUrl: string;
  subDescription: string;
  metaUrl: string;
  metaTitle: string;
  metaDescription: string;
  menuItems: string[];
  thumbnailAltText: string;
  key: string;
  factsSubTitle: string;
  factsDescription: string;
  backgroundAltText: string;
  backgroundUrlWeb: string;
  backgroundUrlMobile: string;
  id: string;
  factsTitle: string;
  title: string;
}

export interface IngredientGroupMetadata {
  id: string;
  title: string;
  items: IngredientGroupItemMetaData[];
}

export interface IngredientGroupItemMetaData {
  id: string;
  ingredients: string[];
}